import CheckIcon from '@mui/icons-material/Check';
import EventIcon from '@mui/icons-material/Event';
import { SxProps, Theme } from '@mui/material/styles';
import { ErrorBoundary } from '@sentry/react';
import React, { useCallback, useContext, useEffect } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';

import {
  ST,
  CallOutcomeEnum,
  Location,
  ServiceTypeEnum,
  SERVICE_TYPES,
  SupportedProviderEnum,
  isEmailOutcome,
  isEmployeeAssignOutcome,
  isNewAppointmentOutcome,
  isPreviousAppointmentOutcome,
  isRequiredNotesOutcome,
  LocationSchema,
  TradeEnum,
  Paths,
  isRequiredCustomerOutcome,
  isRequiredJobOutcome,
  ServiceTitanMappedCall,
  isRequiredServiceTypeOutcome,
  isRequiredTradeOutcome,
  StCapacityPhoneCallCreateDTO,
  StCapacityPhoneCallCreateCtxSchema,
  ProviderVersions
} from '_lib/api/apiTypes.shim';

import { useQueryClient } from '_lib/api/QueryProvider';
import { CompleteCallWrapper } from '_lib/callPop/common/CompleteCallWrapper/CompleteCallWrapper';
import { useCallLogNavigate } from '_lib/callPop/useCallLogNavigate';
import { Box } from '_lib/components/Box/Box';
import { Button } from '_lib/components/Button/Button';
import { Checkbox } from '_lib/components/Checkbox/Checkbox';
import { DebouncedTextField } from '_lib/components/DebouncedTextField/DebouncedTextField';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { Stack } from '_lib/components/Stack/Stack';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';

import { CallDetailsFooter } from '_lib/featureComponents/CallDetailsFooter/CallDetailsFooter';
import { CallOutcomeSelect } from '_lib/featureComponents/CallOutcomeSelect/CallOutcomeSelect';
import { CancelReasonSelect } from '_lib/featureComponents/CancelReasonSelect/CancelReasonSelect';
import { CardBox } from '_lib/featureComponents/CardBox/CardBox';
import { LocationSelect } from '_lib/featureComponents/Location/LocationSelect';
import { PreviousJobSelect } from '_lib/featureComponents/PreviousJobSelect/PreviousJobSelect';
import { ServiceTypeSelect } from '_lib/featureComponents/ServiceType/ServiceTypeSelect';
import { SimpleErrorDisplay } from '_lib/featureComponents/SimpleErrorDisplay/SimpleErrorDisplay';
import { TechAssignCard } from '_lib/featureComponents/TechAssignCard/TechAssignCard';
import { TradeSelect } from '_lib/featureComponents/Trades/TradeSelect';
import { useAlertDialog } from '_lib/hooks/AlertDialogHook';
import { isAfterHoursReviewContext, useAppointmentAfterHoursDialog } from '_lib/hooks/AppointmentAfterHoursDialogHook';
import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';
import { useLocationFormDialog } from '_lib/hooks/LocationFormDialogHook';
import { useTechAssignDialog } from '_lib/hooks/TechAssignDialogHook';

import { useAuthRoles } from '_lib/hooks/useAuthRoles';
import { useAppSelector, useAppDispatch } from '_lib/redux/hooks';
import { addAppListener } from '_lib/redux/listenerMiddleware';
import { isAppointmentAfterHours } from '_lib/services/appointmentService';
import { CustomerContext, PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { DateUtils } from '_lib/utils/date.utils';
import { getOnCallNotes, isLocationEqual, toSxArray } from '_lib/utils/helpers';
import { logDebug } from '_lib/utils/logging';
import { reportException } from '_lib/utils/sentryService';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { ClearIssueItem } from '_lib/utils/validationErrorUtils';

import { zipZoneDialogCheck } from '_lib/utils/zipZoneDialogUtils';

import {
  isNewZoneSelectedPredicate,
  selectZoneZipContext,
  StCapacityCompanyContext
} from '../../../../common/CompanyPane/Company.slice';

import { useGetAvailabilityDialog } from '../../hooks/GetAvailabilityDialogHook';
import {
  initialize,
  isCustomerChangePredicate,
  PhoneCallContext,
  selectCustomerContext,
  selectPhoneCallContext,
  selectPhoneCallFieldIssues,
  selectPhoneCallNonFieldErrors,
  setCustomerContextLocations,
  setPartialPhoneCallContext,
  setPhoneCallFieldIssues,
  setPhoneCallNonFieldErrors
} from '../../StCapacityPhoneCall.slice';

import { BusinessUnitSelect } from './BusinessUnitSelect/BusinessUnitSelect';
import { CapacityItemClearable } from './CapacityItemClearable/CapacityItemClearable';
import { JobTypeSelect } from './JobTypeSelect/JobTypeSelect';

interface StCapacityCallDetailsPaneProps {
  phoneCallURLState: PhoneCallURLState;
  companyContext: StCapacityCompanyContext;
  mappedCall: ServiceTitanMappedCall | null;
  refForDialog: React.RefObject<null>;
  sx?: SxProps<Theme>;
  showFooter?: boolean;
  onSetIsSaving: (isSaving: boolean) => void;
}

// TODO: rethink this!
export const handledIssues: Pick<
  { [k in Paths<StCapacityPhoneCallCreateDTO>]: k },
  | 'appointmentDate'
  | 'notes'
  | 'internalNotes'
  | 'outcome'
  | 'serviceType'
  | 'trade'
  | 'providerData.providerJobId'
  | 'providerData.providerBusinessUnitId'
  | 'providerData.providerCancelReasonId'
  | 'providerData.providerJobTypeId'
  | 'providerData.providerLocationId'
  | 'providerData.providerAssignedEmployeeId'
> = {
  appointmentDate: 'appointmentDate',
  notes: 'notes',
  internalNotes: 'internalNotes',
  outcome: 'outcome',
  serviceType: 'serviceType',
  trade: 'trade',
  'providerData.providerJobId': 'providerData.providerJobId',
  'providerData.providerBusinessUnitId': 'providerData.providerBusinessUnitId',
  'providerData.providerJobTypeId': 'providerData.providerJobTypeId',
  'providerData.providerCancelReasonId': 'providerData.providerCancelReasonId',
  'providerData.providerLocationId': 'providerData.providerLocationId',
  'providerData.providerAssignedEmployeeId': 'providerData.providerAssignedEmployeeId'
} as const;

const capacityIsDisabledMessage = (phoneCallContext: PhoneCallContext, customerContext: CustomerContext | null) => {
  if (!customerContext?.customer || !phoneCallContext.location) {
    return 'Customer and Location must be selected first';
  } else if (
    phoneCallContext.callOutcome === CallOutcomeEnum.NewAppointment &&
    (!phoneCallContext.businessUnit || !phoneCallContext.jobType)
  ) {
    return 'Business Unit and Job Type must be selected first';
  } else if (
    phoneCallContext.callOutcome === CallOutcomeEnum.Reschedule &&
    !phoneCallContext.previousServiceTitanAppointment
  ) {
    return 'Job must be selected first';
  }

  return '';
};

/**
 * @returns Component for recording information and completing call
 */
export const StCapacityCallDetails = (props: StCapacityCallDetailsPaneProps): React.ReactElement => {
  const { phoneCallURLState, companyContext, mappedCall, refForDialog, sx, showFooter, onSetIsSaving } = props;

  const api = useContext(ApiContext);
  const serviceTitanApi = api.serviceTitan(companyContext.company.id);
  const queryClient = useQueryClient();

  const { navigateToCallLog } = useCallLogNavigate();

  const { enqueueSnackbar } = useOurSnackbar();

  const authRoles = useAuthRoles();

  const locationFormDialog = useLocationFormDialog();
  const techAssignDialog = useTechAssignDialog();
  const confirmationDialog = useConfirmationDialog();
  const alertDialog = useAlertDialog();
  const getAvailabilityDialog = useGetAvailabilityDialog();
  const appointmentAfterHoursDialog = useAppointmentAfterHoursDialog();

  /* -------- REDUX -------- */

  const zoneZipContext = useAppSelector(selectZoneZipContext);
  const customerContext = useAppSelector(selectCustomerContext);
  const phoneCallContext = useAppSelector(selectPhoneCallContext);
  const fieldIssues = useAppSelector(selectPhoneCallFieldIssues);
  const nonFieldErrors = useAppSelector(selectPhoneCallNonFieldErrors);

  const dispatch = useAppDispatch();

  /* -------- END REDUX -------- */

  const handlePhoneCallContextUpdate = useCallback(
    (updates: Partial<PhoneCallContext>, clearIssues?: ClearIssueItem[]) => {
      dispatch(setPartialPhoneCallContext({ updates, clearIssues }));
    },
    [dispatch]
  );

  /**
   * TODO: also used in ST API
   */
  const setLocationIfValid = useCallback(
    async (location: Location | undefined, customerLocations: NonNullable<CustomerContext['locations']>) => {
      const result = LocationSchema.safeParse(location);
      if (location && !result.success) {
        const result = await locationFormDialog.show({
          location,
          locationResource: serviceTitanApi.locations
        });
        if (!result || result.action === 'CANCEL') return;
        else {
          const locationResult = result.value;
          const updatedLocations = customerLocations.map((l) =>
            l.providerId !== location.providerId ? l : locationResult
          );

          dispatch(setCustomerContextLocations(updatedLocations));
          handlePhoneCallContextUpdate({ previousServiceTitanAppointment: undefined, location: locationResult }, [
            handledIssues['providerData.providerLocationId']
          ]);
        }
      } else {
        handlePhoneCallContextUpdate(
          { previousServiceTitanAppointment: undefined, location },
          location ? [handledIssues['providerData.providerLocationId']] : undefined
        );
      }
    },
    [dispatch, handlePhoneCallContextUpdate, locationFormDialog, serviceTitanApi.locations]
  );

  useEffect(() => {
    // listens for changes to customer locations to see if the Selected Location has been updated, so that we can then
    // update the selected location along with any other behavior (zone/zip change effects, etc..)
    return dispatch(
      addAppListener({
        actionCreator: setCustomerContextLocations,
        effect: ({ payload: updatedLocations }, api) => {
          logDebug('// - Customer Location List Change Handler ST Capacity - //');
          const phoneCallContext = api.getState().stCapacityPhoneCallSlice.phoneCallContext;
          if (phoneCallContext.location?.providerId) {
            const maybeUpdatedLocation = updatedLocations.find(
              (location) => location.providerId === phoneCallContext.location?.providerId
            );
            if (maybeUpdatedLocation && !isLocationEqual(maybeUpdatedLocation, phoneCallContext.location)) {
              handlePhoneCallContextUpdate({ location: maybeUpdatedLocation });
            } else if (!maybeUpdatedLocation) {
              handlePhoneCallContextUpdate({ location: undefined });
            }
          }
        }
      })
    );
  }, [dispatch, handlePhoneCallContextUpdate]);

  useEffect(() => {
    return dispatch(
      addAppListener({
        predicate: isCustomerChangePredicate,
        effect: async ({ payload: customerCtx }) => {
          // When changing customer, we always reset the following PhoneCallState:
          logDebug('// - Customer Change Handler ST Capacity - //');

          // Every time a customer change happens, we blow away selected job and location
          handlePhoneCallContextUpdate({
            previousServiceTitanAppointment: undefined,
            location: undefined
          });

          // Try to auto set location if only one exists
          const customerLocations = customerCtx?.locations;

          if (customerLocations && customerLocations?.length === 1) {
            const location = customerLocations[0];
            await setLocationIfValid(location, customerLocations);
          }
        }
      })
    );
  }, [companyContext, dispatch, handlePhoneCallContextUpdate, setLocationIfValid]);

  useEffect(() => {
    return dispatch(
      addAppListener({
        predicate: isNewZoneSelectedPredicate,
        effect: ({ payload: companyCtx }) => {
          logDebug('// - New Zone Selected Handler ST Capacity - //');
          if (!companyCtx?.selectedZone) return; // Should be false (see predicate). For ease of TS we'll keep the guard

          // TODO - discuss difficulty with managing zone:
          // - it's a nonFieldError
          // - if it was a fieldIssue, we should be handling/displaying it here
          if (nonFieldErrors?.length) {
            const updatedErrors = nonFieldErrors.filter((msg) => !msg.toLowerCase().includes('zone'));
            if (updatedErrors.length !== nonFieldErrors.length) dispatch(setPhoneCallNonFieldErrors(updatedErrors));
          }
        }
      })
    );
  }, [dispatch, nonFieldErrors]);

  type MultiAppointmentJobStateOverrides = {
    callOutcome?: CallOutcomeEnum;
    providerJobId?: string;
  };

  /**
   * TODO: also used in ST API
   *
   * @param overrides - by default, values of the current PhoneCallState are investigated. Pass in an override to
   * explicitly check a potentially new PhoneCallState value.
   * @returns whether or not the PhoneCallState has providerJobId set such that there is more than one appointment for
   * the job AND the callOutcome is either: Cancellation or Reschedule
   */
  const invalidStateMultiAppointmentJob = async (overrides: MultiAppointmentJobStateOverrides = {}) => {
    const callOutcome = overrides?.callOutcome ? overrides.callOutcome : phoneCallContext?.callOutcome;
    const providerJobId = overrides?.providerJobId
      ? overrides.providerJobId
      : phoneCallContext?.previousServiceTitanAppointment?.providerJobId;

    if (!customerContext || !providerJobId || !callOutcome || !isPreviousAppointmentOutcome(callOutcome)) return false;

    const customerScheduledAppointmentResource = serviceTitanApi.customers.scheduledAppointments(
      customerContext.customer.providerId
    );

    const serviceTitanAppointments = await queryClient.ensureQueryData({
      queryKey: customerScheduledAppointmentResource.cachedQueryKey(companyContext.company.timezone),
      queryFn: () => customerScheduledAppointmentResource.unpagedListCached(companyContext.company.timezone)
    });

    const possibleDupes = serviceTitanAppointments?.filter(
      (serviceTitanAppointment) => serviceTitanAppointment.providerJobId === providerJobId
    );

    return !!possibleDupes && possibleDupes.length > 1;
  };

  /**
   * Displays a warning dialog if the agent selected a job with multiple appointments (Cancel and Reschedule Only)
   */
  const openMultiAppointmentJobWarning = async () => {
    await alertDialog.show({
      variant: 'warning',
      titleText: 'CANNOT MODIFY MULTI-DAY JOBS',
      content:
        'You are attempting to Cancel or Reschedule a multi-day job. This is not currently supported by our system.'
    });
  };

  const handleSetCallOutcome = async (callOutcome: CallOutcomeEnum) => {
    // prevent updating state if we detect an issue with new outcome
    if (await invalidStateMultiAppointmentJob({ callOutcome })) {
      await openMultiAppointmentJobWarning();
      return;
    }

    const phoneCallContextUpdates: Partial<PhoneCallContext> = {
      callOutcome
    };

    if (isEmailOutcome(callOutcome)) phoneCallContextUpdates.sendCompanyEmail = true;

    // reset any error state based on setting outcome:
    const clearIssues: ClearIssueItem[] = [handledIssues.outcome, handledIssues.appointmentDate];

    if (isNewAppointmentOutcome(callOutcome)) {
      phoneCallContextUpdates.capacityItem = undefined;
      phoneCallContextUpdates.previousServiceTitanAppointment = undefined;
      phoneCallContextUpdates.jobType = undefined;
      phoneCallContextUpdates.businessUnit = undefined;
      phoneCallContextUpdates.assignedTech = undefined;
      phoneCallContextUpdates.appointmentTime = undefined;
      clearIssues.push(
        handledIssues['providerData.providerJobId'],
        handledIssues['providerData.providerAssignedEmployeeId'],
        handledIssues['providerData.providerJobTypeId'],
        handledIssues['providerData.providerBusinessUnitId']
      );
    }

    if (callOutcome !== CallOutcomeEnum.Cancellation) {
      clearIssues.push(handledIssues['providerData.providerCancelReasonId']);
    }

    if (!isEmployeeAssignOutcome(callOutcome)) {
      clearIssues.push({ issuePath: handledIssues['providerData.providerAssignedEmployeeId'], matchPartial: true });
    }

    if (!isRequiredCustomerOutcome(callOutcome)) clearIssues.push(handledIssues['providerData.providerLocationId']);
    if (!isRequiredJobOutcome(callOutcome)) clearIssues.push(handledIssues['providerData.providerJobId']);
    if (!isRequiredNotesOutcome(callOutcome)) clearIssues.push(handledIssues.notes);

    handlePhoneCallContextUpdate(phoneCallContextUpdates, clearIssues);
  };

  const handleSetLocation = async (locationProviderId: string) => {
    const location = customerContext?.locations.find((location) => location.providerId === locationProviderId);
    if (location?.providerId !== phoneCallContext.location?.providerId && customerContext?.locations)
      await setLocationIfValid(location, customerContext.locations);
  };

  const handleClearAssignedTech = useCallback(() => {
    handlePhoneCallContextUpdate({ assignedTech: undefined });
  }, [handlePhoneCallContextUpdate]);

  const handleOpenTechAssignDialog = async () => {
    const { company } = companyContext;
    const promise = await techAssignDialog.show({
      container: refForDialog.current,
      companyId: company.id,
      onCallText: getOnCallNotes(company, zoneZipContext?.selectedZone),
      contactScheduleUrl:
        company.isZoned && zoneZipContext?.selectedZone?.contactScheduleUrl
          ? zoneZipContext.selectedZone.contactScheduleUrl
          : company.contactScheduleUrl,
      capacityItem: phoneCallContext.capacityItem
    });

    if (promise?.action === 'CONFIRM') {
      handlePhoneCallContextUpdate({ assignedTech: promise.value }, [
        { issuePath: handledIssues['providerData.providerAssignedEmployeeId'], matchPartial: true }
      ]);
    }
  };

  const handleSetServiceType = (value: ServiceTypeEnum) => {
    handlePhoneCallContextUpdate({ serviceType: value }, [handledIssues.serviceType]);
  };

  const handleSetTrade = (value: TradeEnum) => {
    handlePhoneCallContextUpdate({ trade: value }, [handledIssues.trade]);
  };

  const handleSetCancelReason = (value?: ST.JobCancelReason) => {
    handlePhoneCallContextUpdate(
      { cancelReason: value },
      value ? [handledIssues['providerData.providerCancelReasonId']] : undefined
    );
  };

  const completeCall = async (data: StCapacityPhoneCallCreateDTO) => {
    try {
      onSetIsSaving(true);

      await api.serviceTitanCapacityPhoneCalls.create(data);

      navigateToCallLog();
    } catch (err) {
      // If we somehow pass validation client side and fail on the api, just dump it (for now)
      if (err instanceof BadRequestError) {
        dispatch(setPhoneCallNonFieldErrors(err.responseData.nonFieldErrors));
        dispatch(
          setPhoneCallFieldIssues(
            err.responseData.fieldIssues.filter((issue) => issue.path?.[0] !== 'nonFieldErrors') || []
          )
        );
      }
      reportException(err);
    } finally {
      onSetIsSaving(false);
    }
  };

  const handleCompleteCall = async () => {
    dispatch(setPhoneCallFieldIssues([]));
    dispatch(setPhoneCallNonFieldErrors([]));

    // Mainly for sanity check and ease of TS below. If any of these are wrong, nothing else is worth running
    if (!phoneCallContext.callOutcome) {
      enqueueSnackbar('Call Outcome is Required');
      return;
    }

    if (await invalidStateMultiAppointmentJob()) {
      openMultiAppointmentJobWarning();
      return;
    }

    if (
      isNewAppointmentOutcome(phoneCallContext.callOutcome) &&
      isAfterHoursReviewContext(companyContext) &&
      phoneCallContext.appointmentTime &&
      isAppointmentAfterHours({
        appointmentStart: phoneCallContext.appointmentTime,
        timezone: companyContext.company.timezone
      })
    ) {
      const afterHoursResult = await appointmentAfterHoursDialog.show({
        container: refForDialog.current,
        companyContext,
        callOutcome: phoneCallContext.callOutcome,
        appointmentPartial: { start: phoneCallContext.appointmentTime },
        jobNo: phoneCallContext.previousServiceTitanAppointment?.job?.jobNo,
        assignedTech: phoneCallContext.assignedTech,
        disableEnforceFocus: true,
        dismissable: false
      });

      if (afterHoursResult?.action !== 'CONFIRM') return;
    }

    const company = companyContext.company;

    const providerMappedCall =
      company.dataProvider.providerOptions.tenantId === companyContext.companyProviderData?.originalCompanyTenantId
        ? mappedCall
        : null;

    const result = StCapacityPhoneCallCreateCtxSchema(company).safeParse({
      id: phoneCallURLState.phoneCallId,
      providerType: SupportedProviderEnum.ST_CAPACITY,
      providerVersion: ProviderVersions.v1,
      outcome: phoneCallContext.callOutcome,
      callerId: phoneCallURLState.callerId,
      duration: Date.now() - phoneCallURLState.durationStart,
      companyDNIS: phoneCallURLState.companyDNIS,
      companyId: company.id,
      companySwitched: companyContext.switched,
      companyZoneId: zoneZipContext?.selectedZone?.id || null,
      originalCompany: companyContext.originalCompanyName || null,
      internalNotes: phoneCallContext.internalNotes || null,
      isManualCall: phoneCallURLState.isManualCall,
      notes: phoneCallContext.notes || null,
      sendCompanyEmail: phoneCallContext.sendCompanyEmail,
      sendEscalationEmail: phoneCallContext.sendEscalationEmail,
      serviceType: phoneCallContext.serviceType || null,
      trade: phoneCallContext.trade || null,
      appointmentDate: phoneCallContext?.appointmentTime ? new Date(phoneCallContext.appointmentTime) : null,
      providerData: {
        providerAppointmentId: phoneCallContext?.previousServiceTitanAppointment?.providerId,
        providerAssignedEmployeeId: phoneCallContext?.assignedTech?.providerId,
        providerBusinessUnitId: isNewAppointmentOutcome(phoneCallContext.callOutcome)
          ? phoneCallContext?.businessUnit?.providerId
          : phoneCallContext?.previousServiceTitanAppointment?.job.providerBusinessUnitId,
        providerCancelReasonId: phoneCallContext?.cancelReason?.id?.toString(),
        providerCallId: providerMappedCall?.providerId,
        providerCustomerId: customerContext?.customer.providerId,
        providerJobId: phoneCallContext?.previousServiceTitanAppointment?.providerJobId,
        providerJobTypeId: isNewAppointmentOutcome(phoneCallContext.callOutcome)
          ? phoneCallContext?.jobType?.providerId
          : phoneCallContext?.previousServiceTitanAppointment?.job.providerJobTypeId,
        providerLocationId: phoneCallContext.location?.providerId
      },
      telephonyId: phoneCallURLState.telephonyId || null
    });

    if (result.success && result.data) {
      if (!company.isZoned) return completeCall(result.data);

      await zipZoneDialogCheck({
        companyContext,
        selectedZone: zoneZipContext?.selectedZone || null,
        zipOrPostal: phoneCallContext.location?.zipOrPostal,
        confirmationCallback: () => completeCall(result.data),
        confirmationDialog,
        alertDialog
      });
    } else if (!result.success) {
      dispatch(setPhoneCallFieldIssues(result.error.issues.filter((issue) => issue.path?.[0] !== 'nonFieldErrors')));

      // TODO - Discuss with Dave O. and better "filter" the issues, ie: one's handled explicitly by
      //        `setFieldIssues` & "extras" here
      //
      // NOTE: this also ensures we display any custom path field issues, ie:
      //       for phone call, there are a few cases where a field issue may have path: ['nonFieldErrors']
      const handledIssueValues = Object.values(handledIssues);
      const extraErrors: string[] = result.error.issues
        .filter(({ path }) => !handledIssueValues.find((issue) => issue === path.join('.')))
        .map(({ message }) => message);

      if (extraErrors.length) dispatch(setPhoneCallNonFieldErrors(extraErrors));

      enqueueSnackbar('Phone Call is Invalid!');
    }
  };

  /**
   * Programmatically revert to 'initial' state
   */
  const reInitCallDetailsPane = async () => {
    dispatch(initialize({ company: companyContext.company, options: { keepExistingCallData: true } }));
  };

  return (
    <Box
      sx={[
        (theme) => ({
          bgcolor: theme.palette.callPane.main,
          color: theme.palette.callPane.contrastText,
          display: 'flex',
          flexDirection: 'column'
        }),
        ...toSxArray(sx)
      ]}>
      <ErrorBoundary
        beforeCapture={(scope) => {
          scope.setTag('pane', 'Call Details Pane - Inner');
        }}
        fallback={({ resetError }) => (
          <ErrorBoundaryFallback
            title='Error Displaying Call Details'
            onRefresh={() => {
              reInitCallDetailsPane();
              resetError();
            }}
          />
        )}>
        <Stack
          direction='row'
          sx={{ justifyContent: 'space-between', alignItems: 'center', paddingX: 1.5, paddingY: 2 }}>
          <Typography variant='h4' text='CALL DETAILS' />
        </Stack>
        {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1, margin: 1 }} />}
        <Stack sx={{ paddingX: 1.5, paddingBottom: 1.5, overflow: 'auto', position: 'relative', flexGrow: 1 }}>
          <CardBox>
            <CallOutcomeSelect
              callOutcome={phoneCallContext?.callOutcome}
              issuePath={handledIssues.outcome}
              issues={fieldIssues}
              onSetCallOutcome={handleSetCallOutcome}
            />
          </CardBox>
          <CardBox>
            <LocationSelect
              onSetLocation={handleSetLocation}
              location={phoneCallContext.location}
              locations={customerContext?.locations}
              disabled={!customerContext}
              required={isRequiredCustomerOutcome(phoneCallContext.callOutcome)}
              issuePath={handledIssues['providerData.providerLocationId']}
              issues={fieldIssues}
            />
          </CardBox>
          {phoneCallContext?.callOutcome === CallOutcomeEnum.Cancellation && customerContext && (
            <CardBox>
              <CancelReasonSelect
                companyId={companyContext.company.id}
                cancelReason={phoneCallContext?.cancelReason}
                onSetCancelReason={handleSetCancelReason}
                issuePath={handledIssues['providerData.providerCancelReasonId']}
                issues={fieldIssues}
              />
            </CardBox>
          )}
          {phoneCallContext.callOutcome !== CallOutcomeEnum.NewAppointment && (
            <CardBox>
              <PreviousJobSelect
                issuePath={handledIssues['providerData.providerJobId']}
                issues={fieldIssues}
                companyId={companyContext.company.id}
                timezone={companyContext.company.timezone}
                providerCustomerId={customerContext?.customer.providerId}
                providerLocationId={phoneCallContext?.location?.providerId}
                required={isRequiredJobOutcome(phoneCallContext.callOutcome)}
                onSetServiceTitanAppointment={async (previousServiceTitanAppointment) => {
                  if (
                    await invalidStateMultiAppointmentJob({
                      providerJobId: previousServiceTitanAppointment.providerJobId
                    })
                  ) {
                    openMultiAppointmentJobWarning();
                  } else {
                    const clearIssues: ClearIssueItem[] = [handledIssues['providerData.providerJobId']];

                    const businessUnits = await queryClient.ensureQueryData({
                      queryKey: serviceTitanApi.businessUnits.unpagedListQueryKey({}),
                      queryFn: async () => {
                        const res = await serviceTitanApi.businessUnits.unpagedList({});
                        return res.results;
                      }
                    });
                    const businessUnit = businessUnits?.find(
                      (bu) => bu.providerId === previousServiceTitanAppointment.job.providerBusinessUnitId
                    );
                    if (businessUnit) clearIssues.push(handledIssues['providerData.providerBusinessUnitId']);

                    const jobTypes = await queryClient.ensureQueryData({
                      queryKey: serviceTitanApi.jobTypes.unpagedListQueryKey({}),
                      queryFn: async () => {
                        const res = await serviceTitanApi.jobTypes.unpagedList({});
                        return res.results;
                      }
                    });
                    const jobType = jobTypes?.find(
                      (jt) => jt.providerId === previousServiceTitanAppointment.job.providerJobTypeId
                    );
                    if (jobType) clearIssues.push(handledIssues['providerData.providerJobTypeId']);

                    handlePhoneCallContextUpdate(
                      {
                        previousServiceTitanAppointment,
                        businessUnit,
                        jobType
                      },
                      clearIssues
                    );
                  }
                }}
                previousServiceTitanAppointment={phoneCallContext.previousServiceTitanAppointment}
                onClearPreviousServiceTitanAppointment={() => {
                  handlePhoneCallContextUpdate({
                    previousServiceTitanAppointment: undefined,
                    jobType: undefined,
                    businessUnit: undefined,
                    capacityItem: undefined
                  });
                }}
              />
            </CardBox>
          )}
          {isNewAppointmentOutcome(phoneCallContext.callOutcome) && (
            <>
              <CardBox>
                <JobTypeSelect
                  disabled={!!phoneCallContext.capacityItem}
                  companyId={companyContext.company.id}
                  jobType={phoneCallContext.jobType}
                  onSetJobType={(jobType) => {
                    const updates: Partial<PhoneCallContext> = { jobType };
                    if (
                      phoneCallContext.businessUnit &&
                      !jobType.businessUnitIds?.find((buId) => buId === phoneCallContext.businessUnit?.providerId)
                    ) {
                      updates.businessUnit = undefined;
                    }
                    handlePhoneCallContextUpdate(updates, [handledIssues['providerData.providerJobTypeId']]);
                  }}
                  issuePath={handledIssues['providerData.providerJobTypeId']}
                  issues={fieldIssues}
                />
              </CardBox>
              <CardBox>
                <BusinessUnitSelect
                  disabled={!phoneCallContext.jobType || !!phoneCallContext.capacityItem}
                  jobTypeBusinessUnitIds={phoneCallContext?.jobType?.businessUnitIds}
                  companyId={companyContext.company.id}
                  businessUnit={phoneCallContext.businessUnit}
                  onSetBusinessUnit={(businessUnit) => {
                    const updates: Partial<PhoneCallContext> = { businessUnit };
                    if (
                      phoneCallContext.jobType &&
                      !phoneCallContext.jobType.businessUnitIds?.find((buId) => buId === businessUnit.providerId)
                    ) {
                      updates.jobType = undefined;
                    }
                    handlePhoneCallContextUpdate(updates, [handledIssues['providerData.providerBusinessUnitId']]);
                  }}
                  issuePath={handledIssues['providerData.providerBusinessUnitId']}
                  issues={fieldIssues}
                />
              </CardBox>
            </>
          )}
          {!phoneCallContext.capacityItem && isNewAppointmentOutcome(phoneCallContext.callOutcome) && (
            <CardBox>
              <Tooltip title={capacityIsDisabledMessage(phoneCallContext, customerContext)}>
                <span>
                  <Button
                    disabled={!!capacityIsDisabledMessage(phoneCallContext, customerContext)}
                    variant='contained'
                    color='primary'
                    fullWidth
                    onClick={async () => {
                      // TODO: talk about how restrictive we want this to be
                      if (
                        !phoneCallContext.businessUnit ||
                        !phoneCallContext.jobType ||
                        !phoneCallContext.location ||
                        !customerContext?.customer
                      ) {
                        enqueueSnackbar('Business Unit, Job Type, Customer and Location must be selected first');
                        return;
                      }

                      const result = await getAvailabilityDialog.show({
                        container: refForDialog.current,
                        disableEnforceFocus: true,
                        dismissable: false,
                        businessUnit: phoneCallContext.businessUnit,
                        jobType: phoneCallContext.jobType,
                        customer: customerContext.customer,
                        location: phoneCallContext.location,
                        durationStart: phoneCallURLState.durationStart,
                        companyContext
                      });

                      if (result?.action === 'CONFIRM') {
                        let appointmentTime = DateUtils.setTimeZoneOffset(
                          new Date(result.value.capacityItem.start),
                          companyContext.company.timezone
                        );
                        const now = new Date();

                        if (
                          DateUtils.isSame(now, appointmentTime, 'day', {
                            timezone: companyContext.company.timezone
                          }) &&
                          DateUtils.isAfter(now, appointmentTime)
                        ) {
                          appointmentTime = DateUtils.roundToNextMinuteInterval(now, 30);
                        }

                        handlePhoneCallContextUpdate(
                          {
                            capacityItem: result.value.capacityItem,
                            appointmentTime: appointmentTime.toISOString()
                          },
                          [handledIssues.appointmentDate]
                        );
                      }
                    }}
                    startIcon={<EventIcon />}>
                    Get Availability
                  </Button>
                  <SimpleErrorDisplay
                    issues={fieldIssues}
                    issuePath={handledIssues.appointmentDate}
                    matchPartial={true}
                  />
                </span>
              </Tooltip>
            </CardBox>
          )}
          {phoneCallContext.capacityItem && phoneCallContext.appointmentTime && (
            <CardBox>
              <CapacityItemClearable
                capacityItem={phoneCallContext.capacityItem}
                timezone={companyContext.company.timezone}
                appointmentTime={new Date(phoneCallContext.appointmentTime)}
                onSetAppointmentTime={(appointmentTime) => {
                  handlePhoneCallContextUpdate({ appointmentTime: appointmentTime.toISOString() }, [
                    handledIssues.appointmentDate
                  ]);
                }}
                onClearCapacityItem={() => {
                  handlePhoneCallContextUpdate({
                    capacityItem: undefined,
                    appointmentTime: undefined
                  });
                }}
                issues={fieldIssues}
                issuePath={handledIssues.appointmentDate}
              />
            </CardBox>
          )}
          {(isRequiredTradeOutcome(phoneCallContext.callOutcome) ||
            phoneCallContext.callOutcome === CallOutcomeEnum.UpdateNotes) && (
            <CardBox>
              <TradeSelect
                trade={phoneCallContext.trade}
                onSetTrade={handleSetTrade}
                trades={companyContext.company.callCenter.trades}
                issuePath={handledIssues.trade}
                issues={fieldIssues}
              />
            </CardBox>
          )}
          {isRequiredServiceTypeOutcome(phoneCallContext.callOutcome) && (
            <CardBox>
              <ServiceTypeSelect
                serviceType={phoneCallContext.serviceType}
                onSetServiceType={handleSetServiceType}
                serviceTypes={SERVICE_TYPES}
                issuePath={handledIssues.serviceType}
                issues={fieldIssues}
              />
            </CardBox>
          )}
          {phoneCallContext.callOutcome === CallOutcomeEnum.NewAppointment &&
            companyContext.company.isManualEmployeeAssignEnabled &&
            phoneCallContext.capacityItem && (
              <TechAssignCard
                onClearAssignedTech={handleClearAssignedTech}
                assignedTech={phoneCallContext?.assignedTech}
                issues={fieldIssues}
                issuePath={handledIssues['providerData.providerAssignedEmployeeId']}
                matchPartial={true}
                onOpenTechAssign={handleOpenTechAssignDialog}
              />
            )}
          <CardBox>
            <DebouncedTextField
              fullWidth
              label='Notes'
              required={isRequiredNotesOutcome(phoneCallContext.callOutcome)}
              multiline
              error={{
                issuePath: handledIssues.notes,
                issues: fieldIssues
              }}
              debounceOptions={{ initialValue: phoneCallContext.notes }}
              onChangeHandler={(notes: string) => {
                handlePhoneCallContextUpdate({ notes }, notes ? [handledIssues.notes] : undefined);
              }}
            />
          </CardBox>
          <CardBox>
            <FormControlLabel
              control={
                <Checkbox
                  color='primary'
                  disabled={isEmailOutcome(phoneCallContext.callOutcome)}
                  checked={phoneCallContext.sendCompanyEmail}
                  onChange={(e) => handlePhoneCallContextUpdate({ sendCompanyEmail: !!e.target.checked })}
                />
              }
              label={<Typography text='Send Company Email' />}
            />
          </CardBox>
          <CardBox>
            <DebouncedTextField
              fullWidth
              label='Call Center Internal Notes'
              multiline
              required={phoneCallContext.sendEscalationEmail}
              error={{
                issuePath: handledIssues.internalNotes,
                issues: fieldIssues
              }}
              debounceOptions={{ initialValue: phoneCallContext.internalNotes }}
              onChangeHandler={(internalNotes: string) => {
                handlePhoneCallContextUpdate(
                  { internalNotes },
                  internalNotes ? [handledIssues.internalNotes] : undefined
                );
              }}
            />
          </CardBox>
          {!authRoles.isAccountUser && !authRoles.isAccountAdmin && (
            <CardBox>
              <FormControlLabel
                control={
                  <Checkbox
                    color='primary'
                    checked={phoneCallContext.sendEscalationEmail}
                    onChange={(e) =>
                      handlePhoneCallContextUpdate(
                        { sendEscalationEmail: !!e.target.checked },
                        !e.target.checked ? [handledIssues.internalNotes] : undefined
                      )
                    }
                  />
                }
                label={<Typography text='Send Escalation Email' />}
              />
            </CardBox>
          )}
          <CardBox>
            <FormControlLabel
              control={<Checkbox color='primary' disabled checked={companyContext.switched} />}
              label={<Typography text='Company Switched' />}
            />
          </CardBox>
          <Box>
            <CompleteCallWrapper nonFieldErrors={nonFieldErrors}>
              <Button
                fullWidth
                onClick={handleCompleteCall}
                startIcon={<CheckIcon />}
                color='success'
                variant='contained'>
                Complete Call
              </Button>
            </CompleteCallWrapper>
          </Box>
        </Stack>
        <Divider />
        {showFooter && <CallDetailsFooter />}
        {techAssignDialog.element}
        {locationFormDialog.element}
        {confirmationDialog.element}
        {alertDialog.element}
        {getAvailabilityDialog.element}
        {appointmentAfterHoursDialog.element}
      </ErrorBoundary>
    </Box>
  );
};
